import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigation } from "react-router-dom";
import { RiMoneyDollarCircleFill } from "@remixicon/react";
import _ from "lodash";

import { PricingOptionCard } from "components/pricingoptions/PricingOptionCard";
import DecoratedInputField from "components/InputFieldComponents/DecoratedInputField";
import SubmitButton, { SubmitButtonProps } from "components/SubmitButton";

import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { PricingOptionsDataRoutePath } from "routes/data-routes/billing-data/pricing-options/route.meta";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";

import { cx, fmtCurrency, fmtNumber } from "utils";
import { getMaxSpendingCap } from "utils/planUtils";


const MIN_SPENDING_CAP_USD = Number.parseInt(process.env.REACT_APP_PAY_AS_YOU_GO_MINIMUM_SPENDING_CAP || "6", 10);

function useSpendingCapFromPricingOptions() {
  const { pricingOptions } = usePricingOptions();

  const [ spendingCap, setSpendingCap ] = useState<number>(MIN_SPENDING_CAP_USD);

  useEffect(() => {
    if (pricingOptions?.["pay-as-you-go"]?.creditsCap) {
      setSpendingCap(
        Math.max(
          MIN_SPENDING_CAP_USD,
          Math.ceil((pricingOptions["pay-as-you-go"].creditsCap * 0.60) / 1000)
        )
      );
    }
  }, [ pricingOptions ]);

  return [ spendingCap, setSpendingCap ] as const;
}

function SpendingCapInputs(
  {
    spendingCap,
    setSpendingCap,
    maxSpendingCap
  }: {
    spendingCap: number;
    setSpendingCap: Dispatch<SetStateAction<number>>;
    maxSpendingCap: number | undefined;
  }
) {
  const [ creditsCap, setCreditsCap ] = useState<number>(0);
  const [ actualSpending, setActualSpending ] = useState<number>(0);

  useEffect(() => {
    if (spendingCap) {
      setCreditsCap(_.floor((spendingCap * 1000) / 0.60, -3));
    } else {
      setCreditsCap(0);
    }
  }, [ spendingCap ]);

  useEffect(() => {
    if (creditsCap) {
      setActualSpending((creditsCap / 1000) * 0.60);
    } else {
      setActualSpending(0);
    }
  }, [ creditsCap ]);


  return (
    <div className="flex flex-col gap-y-1 pt-2">
      <span className="text-sm">Spending cap (USD)</span>
      <DecoratedInputField
        type="number"
        min={ MIN_SPENDING_CAP_USD }
        max={ maxSpendingCap }
        value={ spendingCap ?? "" }
        onChange={ (event) => {
          setSpendingCap(event.currentTarget.valueAsNumber);
        } }
        TrailingElement={ <RiMoneyDollarCircleFill className="size-4 text-neutral-300 ml-2" /> }
        invalidTrailingElementClasses="!left-0 !pr-0 w-min"
        className="!pl-7"
        name="pay-as-you-go-cap"
      />
      <input type="hidden"
             name="pay-as-you-go-credits-cap"
             value={ creditsCap }
      />

      <span className="text-xs">
        <>
          {
            (!_.isNaN(spendingCap) && maxSpendingCap && (spendingCap >= MIN_SPENDING_CAP_USD) && (spendingCap <= maxSpendingCap)) &&
              <>You can use up to { fmtNumber(creditsCap) } extra credits for { fmtCurrency(actualSpending) }.</>
          }

          {
            (!spendingCap || (spendingCap < MIN_SPENDING_CAP_USD)) &&
              <span className="text-error-600">You must set the spending cap to at least ${ MIN_SPENDING_CAP_USD }.</span>
          }

          {
            (!_.isNaN(spendingCap) && maxSpendingCap && (spendingCap > maxSpendingCap)) &&
              <span className="text-error-600">Cap exceeds 2x your monthly plan value ({ fmtCurrency(maxSpendingCap) }). Lower it or contact sales.</span>
          }
        </>
      </span>
      <span className="text-xs">Once you reach this limit your scraping will be interrupted</span>
    </div>
  )
}

function FetcherAwareSubmitButton({ disabled, ...props }: SubmitButtonProps) {
  const { pricingOptionsFetcher } = usePricingOptions();
  const navigation = useNavigation();
  const location = useLocation();

  const isNavigatingAway = useMemo(() => {
    return (navigation.state === "loading") && (navigation.location.pathname !== location.pathname);
  }, [ navigation, location.pathname ]);

  return <SubmitButton
    { ...props }
    disabled={ disabled || (pricingOptionsFetcher?.state === "loading" && !!pricingOptionsFetcher?.formData) || isNavigatingAway }
    fetchersToCheck={ [ pricingOptionsFetcher ] }
  />;
}

function CapAwareSubmitButton(
  {
    spendingCap,
    maxSpendingCap,
    disabled,
    ...props
  }: { spendingCap: number, maxSpendingCap: number | undefined } & SubmitButtonProps
) {
  // Upper bound is optional, so $0-priced plans (Enterprise / Custom) aren't blocked.
  const isValidCap = spendingCap >= MIN_SPENDING_CAP_USD &&
    (maxSpendingCap === undefined || spendingCap <= maxSpendingCap);

  return (
    <FetcherAwareSubmitButton
      { ...props }
      disabled={ disabled || !isValidCap }
    />
  );
}

export function PayAsYouGoContent(
  {
    recommended = false,
    primary = true,
    showTitle = true
  } : {
    recommended?: boolean;
    primary?: boolean;
    showTitle?: boolean;
  }
) {
  const formatted1000 = fmtNumber(1000);

  const { pricingOptionsFetcher } = usePricingOptions();
  const [ spendingCap, setSpendingCap ] = useSpendingCapFromPricingOptions();

  const { activeSubscription } = useActiveSubscription();
  const maxSpendingCap = useMemo(() => getMaxSpendingCap(activeSubscription), [ activeSubscription ]);


  return (
    <pricingOptionsFetcher.Form method="POST" action={ PricingOptionsDataRoutePath }>
      <PricingOptionCard
        recommended={ recommended }
        title={ showTitle ? "Enable pay-as-you-go" : undefined }
        body={
          <div className="flex flex-col gap-y-4">
            <div>
              Get additional credits at $0.60 per { formatted1000 } credits plus optional personalized guidance. This prevents your service from being interrupted.
            </div>
            <input type="hidden"
                   name="pay-as-you-go"
                   value="true"
            />
            <label className="grid grid-cols-[min-content,auto] gap-x-2 items-center">
              <input type="radio"
                     name="pay-as-you-go-option"
                     value="pay-as-you-go"
                     className="focus:ring-0"
                     defaultChecked
              />
              <span className="font-semibold text-base">Enable pay-as-you-go</span>
              <span className="col-start-2">Get credits at $0.60 per { formatted1000 } credits</span>
            </label>
            <label className="grid grid-cols-[min-content,auto] gap-x-2 items-center">
              <input type="radio"
                     name="pay-as-you-go-option"
                     value="pay-as-you-go-and-contact-sales"
                     className="focus:ring-0"
              />
              <span className="font-semibold text-base">Enable pay-as-you-go and contact sales</span>
              <span className="col-start-2">Same as above, plus we'll connect you with sales for tailored guidance</span>
            </label>

            <SpendingCapInputs spendingCap={ spendingCap } setSpendingCap={ setSpendingCap } maxSpendingCap={ maxSpendingCap } />
          </div>
        }
        footer={
          <CapAwareSubmitButton
            text="Enable Pay-As-You-Go"
            spendingCap={ spendingCap }
            maxSpendingCap={ maxSpendingCap }
            className={ cx("mt-5 w-full button", primary ? "button-primary" : "button-secondary") }
            size="MD"
            name="pricing-option-action"
            value="pay-as-you-go"
            fetchersToCheck={ [ pricingOptionsFetcher ] }
          />
        }
      />
    </pricingOptionsFetcher.Form>
  );

}

export function ChangePayAsYouGoContent() {
  const { pricingOptionsFetcher } = usePricingOptions();
  const [ spendingCap, setSpendingCap ] = useSpendingCapFromPricingOptions();

  const { activeSubscription } = useActiveSubscription();
  const maxSpendingCap = useMemo(() => getMaxSpendingCap(activeSubscription), [ activeSubscription ]);


  return (
    <pricingOptionsFetcher.Form method="POST" action={ PricingOptionsDataRoutePath }>
      <input type="hidden"
             name="pricing-option-action"
             value="pay-as-you-go"
      />
      <PricingOptionCard
        body={
          <div className="flex flex-col gap-y-4">
            <div className="flex flex-col gap-y-2">
              <div>Increase your spending cap to avoid service interruption.</div>
              <div>Turning off pay-as-you-go or lowering your cap may pause your service if you reach your credit limit before your billing date.</div>
            </div>
            <SpendingCapInputs spendingCap={ spendingCap } setSpendingCap={ setSpendingCap } maxSpendingCap={ maxSpendingCap } />
          </div>
        }
        footer={
          <div className="flex flex-row gap-x-8">
            <FetcherAwareSubmitButton
              text="Disable Pay-as-you-go"
              className="mt-5 w-1/2 button button-secondary"
              size="MD"
              name="pay-as-you-go"
              value="false"
              formNoValidate
              fetchersToCheck={ [ pricingOptionsFetcher ] }
            />
            <CapAwareSubmitButton
              text="Update spending cap"
              spendingCap={ spendingCap }
              maxSpendingCap={ maxSpendingCap }
              className="mt-5 w-1/2 button button-primary"
              size="MD"
              name="pay-as-you-go"
              value="true"
              fetchersToCheck={ [ pricingOptionsFetcher ] }
            />
          </div>
        }
      />
    </pricingOptionsFetcher.Form>
  );
}
